import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ShieldCheck, Pause, Play, Star } from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import { formatDate } from "@/lib/dates";

export default function AdminUsers() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [role, setRole] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (role) params.set("role", role);
    if (q) params.set("q", q);
    try {
      const { data } = await api.get(`/admin/users?${params}`);
      setUsers(data.users || []);
    } finally { setLoading(false); }
  }, [role, q]);
  useEffect(() => { load(); }, [load]);

  const act = async (uid, action) => {
    await api.patch(`/admin/users/${uid}/${action}`);
    load();
  };

  return (
    <DashboardLayout role="admin">
      <div data-testid="admin-users-page">
        <h1 className="font-display text-3xl font-bold text-worker">{t("adminX.users.title")}</h1>

        <div className="mt-6 grid sm:grid-cols-[1fr_200px] gap-3">
          <form onSubmit={(e) => { e.preventDefault(); load(); }}>
            <input className="input-base" placeholder={t("adminX.users.searchPh")} value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-user-search" />
          </form>
          <select className="input-base" value={role} onChange={(e) => setRole(e.target.value)} data-testid="admin-user-role">
            <option value="">{t("adminX.users.allRoles")}</option>
            <option value="client">{t("adminX.users.clients")}</option>
            <option value="provider">{t("adminX.users.providers")}</option>
            <option value="admin">{t("adminX.users.admins")}</option>
          </select>
        </div>

        <div className="mt-6">
          {loading ? <div className="skeleton h-40" /> : (
            <div className="card-base overflow-x-auto" data-testid="users-table">
              <table className="w-full text-sm">
                <thead className="bg-linen">
                  <tr className="text-left text-xs uppercase tracking-widest text-smoke">
                    <th className="p-3">{t("adminX.users.thName")}</th>
                    <th className="p-3">{t("adminX.users.thRole")}</th>
                    <th className="p-3">{t("adminX.users.thStatus")}</th>
                    <th className="p-3 hidden md:table-cell">{t("adminX.users.thRating")}</th>
                    <th className="p-3 hidden md:table-cell">{t("adminX.users.thJoined")}</th>
                    <th className="p-3 text-right">{t("adminX.users.thActions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.user_id} className="border-t border-sand hover:bg-cream cursor-pointer" onClick={() => navigate(`/admin/users/${u.user_id}`)} data-testid={`user-row-${u.user_id}`}>
                      <td className="p-3">
                        <p className="font-display font-semibold text-worker">{u.profile?.full_name || "—"}</p>
                        <p className="text-xs text-smoke font-mono">{u.email}</p>
                      </td>
                      <td className="p-3"><span className="badge">{u.profile?.role || "—"}</span></td>
                      <td className="p-3">
                        <span className={`badge ${u.status === "suspended" ? "bg-danger text-cream" : u.status === "active" ? "bg-success text-cream" : ""}`}>{u.status}</span>
                        {u.profile?.is_verified && <ShieldCheck size={12} className="inline ml-1 text-success" />}
                      </td>
                      <td className="p-3 hidden md:table-cell font-mono">{u.profile?.average_rating || "—"}</td>
                      <td className="p-3 hidden md:table-cell text-xs text-smoke">{formatDate(u.created_at)}</td>
                      <td className="p-3 text-right">
                        <div className="inline-flex gap-1" onClick={(e) => e.stopPropagation()}>
                          {u.status === "active" ? (
                            <button onClick={() => act(u.user_id, "suspend")} className="fx-btn fx-btn--ghost px-2 py-1 text-xs" data-testid={`suspend-${u.user_id}`}><Pause size={14} /></button>
                          ) : (
                            <button onClick={() => act(u.user_id, "reactivate")} className="fx-btn fx-btn--ghost px-2 py-1 text-xs" data-testid={`reactivate-${u.user_id}`}><Play size={14} /></button>
                          )}
                          {u.profile?.role === "provider" && !u.profile?.is_verified && (
                            <button onClick={() => act(u.user_id, "verify")} className="fx-btn fx-btn--ghost px-2 py-1 text-xs" title={t("adminX.users.verifyHint")} data-testid={`verify-${u.user_id}`}><ShieldCheck size={14} /></button>
                          )}
                          {u.profile?.role === "provider" && (
                            <button onClick={() => act(u.user_id, `feature?featured=${!u.profile?.is_featured}`)} className={`fx-btn fx-btn--ghost px-2 py-1 text-xs ${u.profile?.is_featured ? "text-worker" : ""}`} title={u.profile?.is_featured ? t("adminX.users.unfeatureHint") : t("adminX.users.featureHint")} data-testid={`feature-${u.user_id}`}><Star size={14} className={u.profile?.is_featured ? "fill-current" : ""} /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
